/*!
  Pioneer WFRIA MenuItem v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MenuItem"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MenuItem") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MenuItem");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "href", "leftIcon", "rightIcon", "selectedValue", "className", "value", "onClick", "onKeyDown", "onSelect", "itemElement", "itemElementProps", "keyboardNavigateListItem", "withCheckbox", "isIndeterminate", "wrap", "radioMenuButton", "disabled", "menuItemStyles"],
      _excluded2 = ["disableSelectedState"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import Checkbox from "../Checkbox/Checkbox";
import { KEYBOARD_KEYS, MENUITEM } from "../common/constants";
import { MenuItemContext } from "../common/contexts/MenuItemContext";
import { useUniqueId } from "../common/hooks";
import { deprecated, validIcon } from "../common/proptypes";
import KeyboardNavigatorItem from "../private/KeyboardNavigator/KeyboardNavigatorItem";
import { OVERRIDE } from "../themes/common/classes";
import extractTheme from "./extractTheme";
/**
 * MenuItem is a React component that displays a `<li>` with a formatted label and optional left and right
 * icons.
 *
 * @visibleName MenuItem
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
export function MenuItemBase(_ref) {
  let {
    children,
    href,
    // eslint-disable-line no-unused-vars
    leftIcon,
    rightIcon,
    selectedValue,
    className,
    value,
    onClick,
    // eslint-disable-line no-unused-vars
    onKeyDown,
    // eslint-disable-line no-unused-vars
    onSelect,
    // eslint-disable-line no-unused-vars
    itemElement: Element,
    itemElementProps,
    keyboardNavigateListItem,
    withCheckbox,
    isIndeterminate,
    wrap,
    radioMenuButton,
    disabled,
    menuItemStyles
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();

  const {
    disableSelectedState
  } = itemElementProps,
        itemProps = _objectWithoutProperties(itemElementProps, _excluded2);

  const menuitemIconId = useUniqueId("pnr-menuitem-icon-");
  const ariaDescdBy = otherProps["aria-describedby"];
  /**
   * Navigational menu item should not be announced as `pressed`.
   * In order to meet a11y requirement, `pnr-menu-item--selected` class
   * should not be configured for `anchor` element.
   */

  let isSelected = false,
      ariaDescribedBy = null;

  if (Element !== "a") {
    isSelected = selectedValue === value;
  }

  const showSelectedState = isSelected && !disableSelectedState;
  const {
    cssRoot,
    cssmenuItemWrap
  } = extractTheme(_objectSpread({
    theme,
    disabled,
    wrap,
    showSelectedState,
    menuItemStyles
  }, otherProps));

  const handleItemClick = event => {
    if (onClick && !disabled) {
      onClick(event);
    }
    /**
     * `MenuButton` and `Select`, the direct consumers of `MenuItem`, ALWAYS add an `onSelect`
     * implementation. But we don't want to make `onSelect` required, since it is a private prop, and our
     * application consumers create `MenuItem`s on their own without it. So we keep the `if (onSelect)`
     * check, but override the coverage requirement.
     */

    /* istanbul ignore next */


    if (onSelect && !disabled) {
      onSelect(event, value);
    }
  };

  const handleItemKeyDown = event => {
    const {
      onKeyDown: itemOnKeyDown
    } = itemElementProps;
    const {
      key
    } = event;
    const validKeyPressed = key === KEYBOARD_KEYS.SPACE || key === KEYBOARD_KEYS.SPACE_IE11 || key === KEYBOARD_KEYS.ENTER;

    if (validKeyPressed) {
      event.preventDefault();
      /* istanbul ignore else */

      if (href) {
        /**
         * `window.location.assign(href)` is equivalent to `window.location.href = href`.
         * In order to ease unit testing, the function approach is used.
         */
        window.location.assign(href);
      } else if (onSelect) {
        /**
         * `MenuButton` and `Select`, the direct consumers of `MenuItem`, ALWAYS add an `onSelect`
         * implementation. But we don't want to make `onSelect` required, since it is a private prop, and our
         * application consumers create `MenuItem`s on their own without it. So we keep the `if (onSelect)`
         * check, but override the coverage requirement.
         */
        onSelect(event, value);
      }
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
    /**
     * Calling the `handleGroupKeyDown` in MenuButton
     * if it is MenuItem of MenuItemGroup.
     */


    if (itemOnKeyDown) {
      itemOnKeyDown(event);
    }
  };
  /**
   *
   * @param {Object} event event object.
   *
   * This method prevents focusing of disabled item,
   * when the disabled MenuItem is clicked.
   */


  const handleMouseDown = event => {
    event.preventDefault();
  };

  const classes = clsx("pnr-menu-item", OVERRIDE, className, {
    "pnr-menu-item--disabled": disabled,
    "pnr-menu-item--selected": showSelectedState,
    "pnr-menu-item--wrap": wrap
  });
  const {
    menuItemRow,
    menuItemIconLeft,
    menuItemIconRight,
    menuItemLabel,
    menuItemWrapperCSS
  } = menuItemStyles;

  const combinedMenuItemStyle = _objectSpread(_objectSpread({}, cssmenuItemWrap), menuItemLabel);

  const menuItemCheckboxName = useUniqueId("menuItemCheckbox-");
  const item = withCheckbox ? _jsx(Checkbox, {
    decorative: true,
    checked: showSelectedState,
    name: menuItemCheckboxName,
    indeterminate: isIndeterminate,
    className: "pnr-menu-item__label",
    label: children,
    leftIcon: leftIcon,
    rightIcon: rightIcon,
    disabled: disabled,
    labelTextClassName: "wf-u-text-truncate",
    iconId: menuitemIconId
  }) : _jsx(Element, _objectSpread(_objectSpread({}, itemProps), {}, {
    css: menuItemRow,
    className: "pnr-menu-item__row",
    children: _jsxs("span", {
      css: combinedMenuItemStyle,
      className: "pnr-menu-item__label wf-u-text-truncate",
      children: [leftIcon && _jsx("span", {
        css: menuItemIconLeft,
        className: "pnr-menu-item__icon pnr-menu-item__icon-left",
        children: leftIcon
      }), _jsx("span", {
        children: children
      }), rightIcon && _jsx("span", {
        css: menuItemIconRight,
        className: "pnr-menu-item__icon pnr-menu-item__icon-right",
        children: rightIcon
      })]
    })
  }));

  if ((leftIcon || rightIcon) && ariaDescdBy) {
    ariaDescribedBy = `${menuitemIconId} ${ariaDescdBy}`;
  } else if (leftIcon || rightIcon) {
    ariaDescribedBy = menuitemIconId;
  } else if (ariaDescdBy) {
    ariaDescribedBy = ariaDescdBy;
  }

  const elementProps = _objectSpread(_objectSpread({}, otherProps), {}, {
    disabled,
    className: classes,
    onClick: handleItemClick,
    onSelect,
    onMouseDown: disabled ? handleMouseDown : null,
    onKeyDown: handleItemKeyDown,
    "aria-disabled": disabled || null,
    "aria-describedby": ariaDescribedBy
  });

  elementProps.css = _objectSpread(_objectSpread({}, cssRoot), menuItemWrapperCSS);
  return _jsxs(React.Fragment, {
    children: [keyboardNavigateListItem && _jsx(KeyboardNavigatorItem, {
      children: _jsx("li", _objectSpread(_objectSpread({}, elementProps), {}, {
        children: item
      }))
    }), !keyboardNavigateListItem && _jsx("li", _objectSpread(_objectSpread({
      role: radioMenuButton ? "presentation" : null
    }, elementProps), {}, {
      children: _jsx(KeyboardNavigatorItem, {
        children: item
      })
    }))]
  });
}
MenuItemBase.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The value of the menu item. */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** JSX (MenuButton only) / String (MenuButton & Select) to be displayed in the `MenuItem`. */
  children: PropTypes.node,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /**
   * Disables the onClick, and onSelect events on the MenuItem Button.
   */
  disabled: PropTypes.bool,

  /** Url for the `MenuItem` to navigate when clicked. */
  href: PropTypes.string,

  /**
   * Private prop used to render an indeterminate `Checkbox`.
   * Will only be used when `withCheckbox` is true.
   */
  isIndeterminate: PropTypes.bool,

  /**
   * Element to wrap the label and icon.
   */
  itemElement: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),

  /**
   * Props for Element wrapping the label and icon.
   */
  itemElementProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Props for deciding whether `KeyboardNavigatorItem` wraps the `element` or the `<li>`.
   */
  keyboardNavigateListItem: PropTypes.bool,

  /** A React component of type Icon to be placed on the left. See Iconography section. */
  leftIcon: validIcon,

  /**
   * Prop for Element wrapping styles.
   */
  menuItemStyles: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** Callback function fired when the menu item is clicked. */
  onClick: deprecated(PropTypes.func, "Use the `onSelect` prop instead."),

  /**
   * Callback function fired when the menu item is selected.
   * @param {Object}  e Event object.
   * @param {String}  value Value of the menu item selected.
   */
  onSelect: PropTypes.func,

  /**
   * An identifier for RadioButtonGroup.
   */
  radioMenuButton: PropTypes.bool,

  /** A React component of type Icon to be placed on the right. See Iconography section. */
  rightIcon: validIcon,

  /**
   * The actual selected value passed by the Parent.
   */
  selectedValue: PropTypes.string,

  /**
   * Private prop used to render a `Checkbox` along with the `MenuItem`.
   * Used in MultiSelect to render a MenuItem with Checkbox.
   */
  withCheckbox: PropTypes.bool,

  /** Wrap the content if it doesn't fit the width of the container. */
  wrap: PropTypes.bool
} : {};
MenuItemBase.defaultProps = {
  children: null,
  className: "",
  href: null,
  leftIcon: null,
  rightIcon: null,
  selectedValue: null,
  onClick: null,
  onSelect: null,
  itemElement: "div",
  itemElementProps: {},
  keyboardNavigateListItem: false,
  withCheckbox: false,
  isIndeterminate: false,
  wrap: null,
  radioMenuButton: false,
  disabled: false,
  menuItemStyles: {}
};
MenuItemBase.displayName = MENUITEM;

function MenuItem(props) {
  const context = useContext(MenuItemContext);
  return _jsx(MenuItemBase, _objectSpread(_objectSpread(_objectSpread({}, context), props), {}, {
    // Make sure `onSelect` prop of MenuButton is not overwritten.
    onSelect: (event, value) => {
      if (context && context.onSelect) {
        context.onSelect(event, value);
      }

      if (props.onSelect) {
        props.onSelect(event, value);
      }
    }
  }));
}

MenuItem.displayName = MENUITEM;
export default MenuItem;